import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Loader from '../../components/Common/Loader';
import Modal from '../../components/Common/Modal';
import CourseCreate from '../../components/Admin/CourseCreate';
import NxSwitch from '../../components/Common/NxSwitch';
import { CourseIcon } from '../../components/Admin/iconLibrary';
import { shade, CoverPattern } from '../../components/Admin/CourseCover';
import { getStats, getCourses, getCourseStats, createCourse, duplicateCourse, getAdminActivity } from '../../services/adminService';
import { avatarColor, AvatarIcon } from '../../utils/avatar';
import toast from 'react-hot-toast';

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1) return 'только что';
  if (mins < 60) return `${mins} мин назад`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} ч назад`;
  return `${Math.floor(hrs / 24)} дн назад`;
};

const plural = (n, f) => { const a = Math.abs(n) % 100, b = a % 10; if (a > 10 && a < 20) return f[2]; if (b > 1 && b < 5) return f[1]; if (b === 1) return f[0]; return f[2]; };

/* ── Иконки (lucide-стиль) ── */
const IC = {
  users: [['path', { d: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' }], ['circle', { cx: 9, cy: 7, r: 4 }], ['path', { d: 'M23 21v-2a4 4 0 0 0-3-3.87' }], ['path', { d: 'M16 3.13a4 4 0 0 1 0 7.75' }]],
  calendar: [['rect', { x: 3, y: 4, width: 18, height: 18, rx: 2 }], ['path', { d: 'M16 2v4' }], ['path', { d: 'M8 2v4' }], ['path', { d: 'M3 10h18' }], ['path', { d: 'M8 14h8' }]],
  file: [['path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }], ['path', { d: 'M14 2v6h6' }], ['path', { d: 'M9 13h6' }], ['path', { d: 'M9 17h4' }]],
  chart: [['path', { d: 'M18 20V10' }], ['path', { d: 'M12 20V4' }], ['path', { d: 'M6 20v-6' }], ['path', { d: 'M2 20h20' }]],
  book: [['path', { d: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20' }], ['path', { d: 'M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z' }]],
  cap: [['path', { d: 'M22 10 12 5 2 10l10 5 10-5z' }], ['path', { d: 'M6 12.5V17c0 1.7 2.7 3 6 3s6-1.3 6-3v-4.5' }], ['path', { d: 'M22 10v5' }]],
  zap: [['path', { d: 'M13 2 4 14h6l-1 8 9-12h-6l1-8z' }]],
  search: [['circle', { cx: 11, cy: 11, r: 7 }], ['path', { d: 'M21 21l-4.5-4.5' }]],
  plus: [['path', { d: 'M12 5v14' }], ['path', { d: 'M5 12h14' }]],
  chevron: [['polyline', { points: '9 18 15 12 9 6' }]],
  layers: [['path', { d: 'M12 2 2 7l10 5 10-5-10-5z' }], ['path', { d: 'M2 17l10 5 10-5' }], ['path', { d: 'M2 12l10 5 10-5' }]],
  bookOpen: [['path', { d: 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z' }], ['path', { d: 'M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z' }]],
};
const Icon = ({ name, size = 17, sw = 1.7 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    {(IC[name] || []).map((p, i) => React.createElement(p[0], { key: i, ...p[1] }))}
  </svg>
);

const RU_WD = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

/* ── Карточка «Ближайшие уроки курса» (дизайн 1d — дайджест по дням) ── */

// Дата всегда в формате ДД.ММ; под ней — день недели и время урока.
const nxDate = (date) => {
  const d = new Date(date);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const nxSub = (date, time) => {
  const d = new Date(date);
  const t = time ? String(time).slice(0, 5) : null;
  return [RU_WD[d.getDay()], t].filter(Boolean).join(' · ');
};

const NxDate = ({ date, time, hero }) => (
  <div className={`nx-date${hero ? ' hero' : ''}`}>
    <span className="nx-date-d">{date ? nxDate(date) : '—'}</span>
    <span className="nx-date-s">{date ? nxSub(date, time) : 'без даты'}</span>
  </div>
);

// Тип урока → подпись чипа, цвет и вкладка редактора курса
const nxKind = (l) => (
  l.isVariant ? { label: 'Вариант', cls: 'blue', dot: '#3b82f6', tab: 'variants', fallback: 'Варианты' }
    : l.isHomeworkOnly ? { label: 'ДЗ', cls: 'green', dot: '#22c55e', tab: 'homework', fallback: 'Без модуля' }
      : { label: 'Вебинар', cls: 'amber', dot: '#f59e0b', tab: 'sections', fallback: 'Без модуля' }
);

// Обложка курса: приоритет — выбранные админом цвет+иконка; иначе — по типу экзамена.
const coverFor = (course) => {
  const color = course?.color, icon = course?.icon, examType = course?.examType;
  if (color) return { g: [shade(color, -30), color], mark: icon || 'zap', b1: 'rgba(255,255,255,.5)', b2: shade(color, 34) + 'cc' };
  if (examType === 'ЕГЭ') return { g: ['#3B1E8F', '#8B5CF6'], mark: 'cap', b1: 'rgba(255,255,255,.5)', b2: 'rgba(56,189,248,.55)' };
  if (examType === 'ОГЭ') return { g: ['#0B3B45', '#14B8A6'], mark: 'book', b1: 'rgba(255,255,255,.45)', b2: 'rgba(132,204,22,.5)' };
  return { g: ['#3B0764', '#C026D3'], mark: 'zap', b1: 'rgba(255,255,255,.5)', b2: 'rgba(251,113,133,.55)' };
};

export default function DashboardPage() {
  const { can } = useAuth();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [courseStats, setCourseStats] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [nc, setNc] = useState({ name: '', examType: 'ЕГЭ', color: '#3dffa1', icon: 'star', draft: true, copyFrom: '' });
  const [creating, setCreating] = useState(false);
  const [nextFilter, setNextFilter] = useState('all'); // фильтр карточки «Ближайшие уроки»
  const [activity, setActivity] = useState([]);            // лента «Последние действия»
  const [draftsMode, setDraftsMode] = useState(false);      // полка черновиков вместо активных курсов
  const [actOpen, setActOpen] = useState(false);            // окно «Все действия»
  const [allAct, setAllAct] = useState({ items: [], more: true, total: null, loading: false });
  const actBodyRef = useRef(null);
  const actTailRef = useRef(null);

  /* «Все действия» — вся история, а не 12 последних. Грузим порциями по 50 и
     догружаем, когда прокрутка подходит к концу. Порции могут пересекаться на
     событиях с одной датой — склеиваем по key без дублей. */
  const ACT_PAGE = 50;
  // Последнее состояние ленты и флаг загрузки держим в ref: курсор и защиту от
  // двойной загрузки нужно знать сразу, а не после следующей отрисовки
  const allActRef = useRef(allAct);
  useEffect(() => { allActRef.current = allAct; }, [allAct]);
  const actLoadingRef = useRef(false);

  const loadMoreAct = React.useCallback(async (reset = false) => {
    const cur = allActRef.current;
    if (actLoadingRef.current || (!reset && !cur.more)) return;
    actLoadingRef.current = true;
    const cursor = reset ? null : (cur.items[cur.items.length - 1]?.at || null);
    const commit = (next) => { allActRef.current = next; return next; };   // ref свежий сразу, без ожидания отрисовки
    setAllAct(prev => commit({ ...(reset ? { items: [], more: true, total: null } : prev), loading: true }));
    try {
      const res = await getAdminActivity(ACT_PAGE, cursor);
      setAllAct(prev => {
        const base = reset ? [] : prev.items;
        const map = new Map(base.map(x => [x.key, x]));
        (res.data || []).forEach(x => map.set(x.key, x));
        const items = [...map.values()].sort((a, b) => new Date(b.at) - new Date(a.at));
        return commit({
          items,
          more: !!res.hasMore && items.length > base.length,   // порция ничего не добавила — дальше пусто
          total: res.total ?? prev.total,
          loading: false,
        });
      });
    } catch {
      setAllAct(prev => commit({ ...prev, loading: false, more: false }));
    } finally {
      actLoadingRef.current = false;
    }
  }, []);

  useEffect(() => { if (actOpen) loadMoreAct(true); }, [actOpen, loadMoreAct]);

  // Докрутили до конца списка — берём следующую порцию
  useEffect(() => {
    if (!actOpen) return;
    const root = actBodyRef.current, tail = actTailRef.current;
    if (!root || !tail || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMoreAct(); }, { root, rootMargin: '200px' });
    io.observe(tail);
    return () => io.disconnect();
  }, [actOpen, allAct.items.length, loadMoreAct]);
  const location = useLocation();
  // Полки лежат рядом в одном flex-ряду, поэтому «окно» тянулось бы по самой
  // высокой из них и под короткой полкой висела бы пустота. Держим высоту окна
  // равной высоте видимой полки и анимируем её вместе со сдвигом.
  const listPaneRef = useRef(null);
  const draftsPaneRef = useRef(null);
  const [swipeH, setSwipeH] = useState(0);
  const navigate = useNavigate();

  const openCreate = () => { setNc({ name: '', examType: 'ЕГЭ', color: '#3dffa1', icon: 'star', draft: true, copyFrom: '' }); setCreateOpen(true); };
  const submitCreate = async () => {
    if (!nc.name.trim()) { toast.error('Введите название курса'); return; }
    setCreating(true);
    try {
      let res;
      if (nc.copyFrom) {
        res = await duplicateCourse(nc.copyFrom, { name: nc.name.trim() });
        toast.success('Структура скопирована — курс создан как черновик');
      } else {
        res = await createCourse({ name: nc.name.trim(), examType: nc.examType || null, color: nc.color, icon: nc.icon, isActive: !nc.draft });
        toast.success(nc.draft ? 'Черновик создан' : 'Курс создан');
      }
      navigate(`/admin/course/${res.data.id}`);
    } catch { toast.error('Ошибка при создании курса'); }
    finally { setCreating(false); }
  };

  // Переход из «Быстрых действий» в профиле — сразу открываем окно создания курса
  useEffect(() => {
    if (!location.state?.createCourse) return;
    openCreate();
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state]); // eslint-disable-line

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, coursesRes] = await Promise.all([getStats(), getCourses()]);
        setStats(statsRes.data);
        getAdminActivity(12).then(r => setActivity(r.data || [])).catch(() => {});
        const cs = coursesRes.data || [];
        setCourses(cs);
        const pairs = await Promise.all(cs.map(c => getCourseStats(c.id).then(r => [c.id, r.data]).catch(() => [c.id, null])));
        const map = {}; pairs.forEach(([cid, d]) => { map[cid] = d; });
        setCourseStats(map);
        // по умолчанию выбираем опубликованный курс — черновики живут на отдельной полке
        setSelectedId((cs.find(c => c.isActive !== false) || cs[0])?.id ?? null);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  // Следим за высотой обеих полок: меняется при поиске, загрузке статистики и
  // переносе карточек на другую строку при ресайзе окна.
  useLayoutEffect(() => {
    const measure = () => {
      const el = draftsMode ? draftsPaneRef.current : listPaneRef.current;
      if (el) setSwipeH(el.offsetHeight);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) { [listPaneRef.current, draftsPaneRef.current].forEach(el => el && ro.observe(el)); }
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [draftsMode, loading, courses, courseStats, search]);

  if (loading) return <Loader />;

  const q = search.trim().toLowerCase();
  const matches = (c) => !q || (c.name || '').toLowerCase().includes(q);
  // Черновики живут на отдельной полке, в основной сетке — только опубликованные
  const published = courses.filter(c => c.isActive !== false);
  const drafts = courses.filter(c => c.isActive === false);
  const shelf = published.filter(matches);
  const draftShelf = drafts.filter(matches);
  const active = courses.find(c => c.id === selectedId) || null;
  const aStats = active ? courseStats[active.id] : null;
  // «Ближайшие уроки» — расписание-напоминание: сверху самый близкий будущий урок
  // («Следующий»), ниже — что идёт за ним. Если будущих нет вовсе, показываем недавние,
  // чтобы карточка не пустовала.
  const allLessons = aStats?.lessonStats || [];
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const matchFilter = (l) => (
    nextFilter === 'web' ? (!l.isHomeworkOnly && !l.isVariant)
      : nextFilter === 'hw' ? !!l.isHomeworkOnly
        : true
  );
  const withDate = allLessons.filter(l => l.date);
  const filteredLessons = withDate.filter(matchFilter);
  const upcoming = filteredLessons.filter(l => new Date(l.date) >= todayStart).sort((a, b) => new Date(a.date) - new Date(b.date));
  const past = filteredLessons.filter(l => new Date(l.date) < todayStart).sort((a, b) => new Date(b.date) - new Date(a.date));
  const nextLesson = upcoming[0] || null;
  // Высота карточки не должна прыгать: блок «Следующий» примерно как две строки,
  // поэтому под ним показываем 3 урока, а без него — 5.
  const laterLessons = nextLesson ? upcoming.slice(1, 4) : past.slice(0, 5);
  const laterLabel = nextLesson ? 'Дальше в расписании' : 'Недавние';
  const nextOptions = [
    { key: 'all', label: 'Все' },
    { key: 'web', label: 'Вебинары' },
    { key: 'hw',  label: 'ДЗ' },
  ];
  // «Все уроки» ведёт в редактирование курса на ту же вкладку, что выбрана в тумблере.
  // Если передан урок — редактор сразу открывает именно его.
  const openCourse = (tab, lessonId) => {
    if (!active) return;
    navigate(`/admin/course/${active.id}`, { state: { tab, lessonId } });
  };
  const openCourseTab = () => openCourse(nextFilter === 'hw' ? 'homework' : 'sections');



  // Строка ленты «Последние действия» — одна и та же в карточке и в окне «Все действия»
  const actRow = (a, withBorder) => {
    const s = a.student;
    return (
      <div key={a.key || `${a.kind}-${s.id}-${a.at}`} className="dash-act"
        onClick={() => navigate('/admin/students', { state: { openStudentId: s.id } })}
        title={`Открыть карточку — ${s.name || s.email || 'ученик'}`}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 8px', borderBottom: withBorder ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}>
        <div style={{ width: 40, height: 40, borderRadius: 13, flex: 'none', background: avatarColor(s.id), boxShadow: `0 6px 14px -6px ${avatarColor(s.id)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {s.avatar ? <img src={s.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <AvatarIcon id={s.id} size={21} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name || s.email || 'Без имени'}</div>
          <div className={`dash-act-txt${a.kind === 'course_added' ? ' good' : ''}`} style={{ fontSize: 11, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.text}</div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', flex: 'none' }}>{timeAgo(a.at)}</div>
      </div>
    );
  };

  const renderCourseCard = (c) => {
            const on = c.id === selectedId;
            const st = courseStats[c.id] || {};
            const lessons = st.totalLessons ?? (st.lessonStats?.length || 0);
            const sections = st.sectionsCount ?? new Set((st.lessonStats || []).map(l => l.section)).size;
            const empty = lessons === 0;
            const badgeColor = c.color || '#7c4dff';
            const level = [c.examType, c.type].filter(Boolean).join(' · ') || 'Курс';
            return (
              <div key={c.id} onClick={() => setSelectedId(c.id)}
                className={`cc-card${on ? ' on' : ''}`}
                style={{
                  boxSizing: 'border-box', borderRadius: 18, overflow: 'hidden', cursor: 'pointer',
                  transition: 'box-shadow .18s, border-color .18s',
                  background: 'var(--card)',
                  border: '2px solid var(--border)',
                  boxShadow: 'var(--shadow-sm)',
                }}>
                {/* Обложка: лаванда + минималистичные macOS-иконки; заголовок прижат вниз */}
                <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', padding: '14px 16px 15px', minHeight: 170, overflow: 'hidden', background: 'var(--cc-bg)' }}>
                  <CoverPattern id={c.id} />
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 11px', borderRadius: 20, background: 'var(--cc-badge-bg)', fontSize: 11, fontWeight: 800, letterSpacing: '.04em', color: 'var(--cc-badge-text)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: badgeColor }} />{level}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 11, background: 'var(--cc-chip-bg)', color: 'var(--cc-chip-ic)', boxShadow: '0 4px 12px -6px rgba(60,30,120,.4)', flex: 'none' }}>
                      <CourseIcon icon={c.icon || 'zap'} size={17} sw={2} />
                    </span>
                  </div>
                  {/* Название прижато вниз; справа — стрелка в редактор курса (клик по карточке только выбирает курс) */}
                  <div style={{ position: 'relative', marginTop: 'auto', paddingTop: 18, display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0, fontFamily: "'Unbounded','Montserrat',sans-serif", fontVariationSettings: "'wght' 800", fontWeight: 800, fontSize: 20, lineHeight: 1.1, letterSpacing: '-.4px', textTransform: 'uppercase', color: 'var(--cc-title)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {c.name}
                    </div>
                    <button type="button" className="dash-cover-open"
                      title={empty ? 'Заполнить курс' : 'Открыть курс'} aria-label={empty ? 'Заполнить курс' : 'Открыть курс'}
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/course/${c.id}`); }}>
                      <Icon name="chevron" size={18} sw={2.3} />
                    </button>
                  </div>
                </div>
                {/* Подвал — как у карточек «Статистики»: слева разделы, справа уроки */}
                <div className="st-crs-body">
                  <div className="st-crs-cell">
                    <div className="st-crs-numrow">
                      <span className="st-crs-pic"><Icon name="layers" size={20} sw={1.9} /></span>
                      <div className="st-crs-num">{sections}</div>
                    </div>
                    <div className="st-crs-cap">{plural(sections, ['раздел', 'раздела', 'разделов'])}</div>
                  </div>
                  <div className="st-crs-cell right">
                    <div className="st-crs-numrow">
                      <div className="st-crs-num">{lessons}</div>
                      <span className="st-crs-pic"><Icon name="bookOpen" size={20} sw={1.9} /></span>
                    </div>
                    <div className="st-crs-cap">{plural(lessons, ['урок', 'урока', 'уроков'])}</div>
                  </div>
                </div>
              </div>
            );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`
        /* На узком экране — одна колонка. Именно minmax(0, 1fr), а не 1fr: иначе длинные
           строки без переноса (имена, «Записан на курс…») распирают карточки шире экрана */
        @media (max-width: 900px) { .dash-bottom { grid-template-columns: minmax(0, 1fr) !important; } .dash-bottom > * { min-width: 0; } }
        @media (max-width: 640px) { .dash-course-cells { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 380px) { .dash-course-cells { grid-template-columns: 1fr !important; } }
      `}</style>
      {/* Заголовок (дату у колокольчика убрали) */}
      <div>
        <h1 className="s-page-title">Главная</h1>
        <p className="s-page-sub">Общая картина вашей платформы</p>
      </div>

      {/* Полка курсов */}
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>Курсы</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', background: 'var(--primary-light)', padding: '4px 10px', borderRadius: 20 }}>
              {draftsMode
                ? `${drafts.length} ${plural(drafts.length, ['черновик', 'черновика', 'черновиков'])}`
                : `${published.length} ${plural(published.length, ['активный', 'активных', 'активных'])}`}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px', color: 'var(--text-3)' }}>
              <Icon name="search" size={14} sw={1.9} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск курса"
                style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 13, color: 'var(--text-1)', width: 120 }} />
            </div>
            <button onClick={() => setDraftsMode(v => !v)}
              className={`dash-drafts-btn${draftsMode ? ' on' : ''}`}
              title={draftsMode ? 'Вернуться к активным курсам' : 'Черновики курсов'}
              aria-label={draftsMode ? 'Вернуться к активным курсам' : 'Черновики курсов'}>
              {draftsMode
                ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                : <Icon name="file" size={17} sw={1.9} />}
            </button>
            {can('courses.edit') && (
              <button onClick={openCreate}
                className="action-btn purple" style={{ padding: '9px 14px', fontSize: 13 }}>
                <Icon name="plus" size={15} sw={2.1} /> Создать курс
              </button>
            )}
          </div>
        </div>

        {/* Две полки в одном «окне»: активные курсы ⇄ черновики, переезд плавным сдвигом */}
        <div className="dash-swipe-win" style={{ height: swipeH ? `${swipeH}px` : undefined }}>
        <div className="dash-swipe" style={{ transform: draftsMode ? 'translateX(-50%)' : 'none' }}>
        <div className="dash-swipe-pane" ref={listPaneRef}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 16 }}>
          {shelf.map(renderCourseCard)}
          {/* Карточка «Создать курс» + подсказка про копирование структуры */}
          {can('courses.edit') && <div onClick={openCreate} className="dash-create"
            style={{ boxSizing: 'border-box', borderRadius: 18, border: '1.5px dashed rgba(124,77,255,.35)', background: 'var(--primary-hover)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 9, color: 'var(--primary-dark)', cursor: 'pointer', minHeight: 200, padding: '18px 16px', textAlign: 'center' }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="plus" size={20} sw={2} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Создать курс</span>
            <span style={{ fontSize: 11.5, color: 'var(--text-3)', fontWeight: 500, lineHeight: 1.35 }}>или скопировать структуру существующего</span>
          </div>}
        </div>
        </div>

        {/* ── Полка черновиков ── */}
        <div className="dash-swipe-pane" ref={draftsPaneRef}>
          {draftShelf.length === 0 ? (
            <div className="dash-drafts-empty">{q ? 'Ничего не найдено' : 'Черновиков нет — все курсы опубликованы'}</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 16 }}>
              {draftShelf.map(renderCourseCard)}
            </div>
          )}
        </div>
        </div>
        </div>
      </div>

      {/* Низ: последние уроки курса + последние действия */}
      <div className="dash-bottom" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 18 }}>
        <div className="section-card dash-next">
          <div className="nx-head">
            <div style={{ minWidth: 0 }}>
              <div className="panel-cap nomb">Уроки курса</div>
            </div>
            <NxSwitch value={nextFilter} onChange={setNextFilter} options={nextOptions} />
          </div>

          <div className="nx-body">
          {!nextLesson && laterLessons.length === 0 ? (
            <div className="nx-empty">
              {nextFilter === 'hw' ? 'Домашних заданий с датой нет' : nextFilter === 'web' ? 'Вебинаров с датой нет' : 'Уроков нет'}
            </div>
          ) : (
            <>
              {nextLesson && (() => {
                const k = nxKind(nextLesson);
                return (
                  <>
                    <div className="nx-sep accent"><span>Следующий</span><i /></div>
                    <div className="nx-hero" onClick={() => openCourse(k.tab, nextLesson.id)} title="Открыть урок в редакторе курса">
                      <NxDate date={nextLesson.date} time={nextLesson.time} hero />
                      <div className="nx-hero-main">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="nx-name hero">{nextLesson.title}</div>
                          <div className="nx-module">{nextLesson.section || k.fallback}</div>
                        </div>
                        <span className={`pg-chip ${k.cls}`} style={{ flex: 'none' }}>{k.label}</span>
                        <span className="nx-open">
                          Открыть
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                        </span>
                      </div>
                    </div>
                  </>
                );
              })()}

              {laterLessons.length > 0 && (
                <>
                  <div className={`nx-sep${!nextLesson ? ' solo' : ''}`}><span>{laterLabel}</span><i /></div>
                  <div className="nx-list">
                    {laterLessons.map(l => {
                      const k = nxKind(l);
                      return (
                        <div key={l.id} className="nx-row">
                          <NxDate date={l.date} time={l.time} />
                          <div className="nx-row-main">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="nx-name">{l.title}</div>
                              <div className="nx-module">{l.section || k.fallback}</div>
                            </div>
                            <span className={`pg-chip ${k.cls}`} style={{ flex: 'none' }}>{k.label}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
          </div>

          <div className="nx-foot">
            <button onClick={openCourseTab} disabled={!active}>Все уроки →</button>
          </div>
        </div>

        <div className="section-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="panel-cap">Последние действия</div>
          {activity.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-3)', fontSize: 13 }}>Пока ничего не происходило</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {activity.slice(0, 5).map((a, i, arr) => actRow(a, i < arr.length - 1))}
            </div>
          )}
          <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <button className="dash-act-more" onClick={() => setActOpen(true)} disabled={activity.length === 0}
              style={{ background: 'none', border: 'none', color: activity.length ? 'var(--primary)' : 'var(--text-3)', fontWeight: 600, fontSize: 13, cursor: activity.length ? 'pointer' : 'default', padding: 0, fontFamily: 'inherit' }}>Все действия →</button>
          </div>
        </div>
      </div>

      {/* Окно «Все действия» — вся лента целиком */}
      <Modal bare isOpen={actOpen} onClose={() => setActOpen(false)}>
        <div className="dash-act-modal" onClick={e => e.stopPropagation()}>
          <div className="dash-act-head">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-1)', letterSpacing: '-0.3px' }}>Все действия</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                {allAct.total != null
                  ? `${allAct.total} ${plural(allAct.total, ['событие', 'события', 'событий'])} по ученикам и их курсам`
                  : 'Загружаем историю…'}
              </div>
            </div>
            <button className="adm-x" onClick={() => setActOpen(false)} aria-label="Закрыть">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div className="dash-act-body" ref={actBodyRef}>
            {allAct.items.length === 0 && !allAct.loading && !allAct.more
              ? <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-3)', fontSize: 13 }}>Пока ничего не происходило</div>
              : allAct.items.map((a, i, arr) => actRow(a, i < arr.length - 1))}
            {/* Хвост списка: когда он показался — догружаем следующую порцию */}
            <div ref={actTailRef} style={{ height: 1 }} />
            {allAct.loading && (
              <div style={{ textAlign: 'center', padding: '14px 0 6px', color: 'var(--text-3)', fontSize: 12 }}>Загружаем…</div>
            )}
            {!allAct.loading && !allAct.more && allAct.items.length > 0 && (
              <div style={{ textAlign: 'center', padding: '14px 0 6px', color: 'var(--text-3)', fontSize: 12 }}>Это все события</div>
            )}
          </div>
        </div>
      </Modal>

      {/* Окно создания курса — тот же каркас, что и «Настройки курса» */}
      <Modal bare isOpen={createOpen} onClose={() => setCreateOpen(false)}>
        <div style={{ width: 'min(640px, 100%)', margin: 'auto' }}>
          <CourseCreate
            value={nc}
            onChange={setNc}
            courses={courses}
            creating={creating}
            onSubmit={submitCreate}
            onClose={() => setCreateOpen(false)}
          />
        </div>
      </Modal>
    </div>
  );
}
